import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Box, useTheme } from '@mui/material';

/**
 * Strip HTML tags from content for plain text previews.
 * Useful for list items where truncating HTML would break tags.
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

// Configure DOMPurify for safe email rendering
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li',
  'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img', 'hr', 'pre', 'code'
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style',
  'colspan', 'rowspan', 'cellpadding', 'cellspacing', 'border'
];

// Force links to open in new tab for security
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

interface SafeHtmlProps {
  content: string;
  /** Render inside a forced white-background container for dark mode readability (e.g. third-party emails) */
  forceLight?: boolean;
}

export function SafeHtml({ content, forceLight }: SafeHtmlProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
    });
  }, [content]);

  const linkColor = forceLight && isDark ? '#1C90D9' : 'primary.main';

  return (
    <Box
      sx={{
        mt: 0.5,
        ...(forceLight && isDark && {
          bgcolor: '#FFFFFF',
          color: '#393939',
          borderRadius: 1,
          p: 1.5,
        }),
        '& a': { color: linkColor },
        '& img': { maxWidth: '100%', height: 'auto' },
        '& table': { borderCollapse: 'collapse' },
        '& td, & th': { padding: '4px 8px' },
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}
